import { z } from '@common/schema/extensions/zod.js';

const displayModeDescription = `The group's display mode.
    - 'hidden': The group's consents shall not be displayed.
    - 'text': The group's consents shall be displayed without checkbox or interaction possibilities.
    - 'list': The group's consents shall be displayed as a list, e.g., bulletpoints.

  For all display modes the 'granted' value will automatically be set to the value of 'grantedDefault'.`;

export enum DisplayMode {
  HIDDEN = 'hidden',
  LIST = 'list',
  TEXT = 'text',
}

export const DisplayModeSchema = z.enum(DisplayMode).optional()
  .describe(displayModeDescription)
  .openapi('DisplayMode');

/** The generation of a page or consent-config.
 * Or the max generation as supported by a service provider.
 * Please note that generation values are numerically ordered and thus
 * might be subject to comparison operations, e.g., `CLASSIC` < `UNLOCK` < `FUTURE`.
 */
export enum Generation {
  CLASSIC = 1,
  UNLOCK = 2,
}

export const GenerationSchema = z.enum(Generation)
  .describe(`The consent config's generation. ${Generation.CLASSIC}=classic/default.`);

export enum Modeconstraint {
  VIEW = 'view',
  EDIT = 'edit',
}

export const ModeconstraintSchema = z.enum(Modeconstraint).optional()
  .openapi('Modeconstraint')
  .describe('View for read-only predecessor (deprecated) consents, edit for editable successor consents.');

export enum ActionName {
  DENYALL_OPTOUT = 'denyall-optout',
  DENYALL_OPTIN = 'denyall-optin',
}

export const ActionNameSchema = z.enum(ActionName)
  .openapi('GroupActionName')
  .describe('The name of the group action.');

export enum FeatureName {
  CHANNELS = 'channels',
  DATATYPES = 'dataTypes',
  PRODUCTTYPES = 'productTypes',
  PURPOSETYPES = 'purposeTypes',
}

export const featureNames = Object.values(FeatureName);

/**
 * Creates a mapped object of optional feature field schemas keyed by FeatureName.
 *
 * @param featureFieldValueSchema - The Zod schema for a single feature field value (e.g., an array schema).
 * @returns An object with each FeatureName mapped to an optional version of the given schema.
 *
 * @example
 * ```ts
 * const featureFields = createFeatureFields(ConsentFeatureRefListSchema);
 * // { channels: ZodOptional<ZodArray<...>>, dataTypes: ZodOptional<ZodArray<...>>, ... }
 * ```
 */
export function createFeatureFields<T extends z.ZodTypeAny>(
  featureFieldValueSchema: T,
): { [K in FeatureName]: z.ZodOptional<T> } {
  return Object.values(FeatureName).reduce((acc, name) => ({
    ...acc,
    [name]: featureFieldValueSchema.optional(),
  }), {} as { [K in FeatureName]: z.ZodOptional<T> });
}
